import type { CcPreviewCampaign, CcSuggestion } from './types';

export const CC_SUGGESTIONS: CcSuggestion[] = [
  {
    id: 1,
    service: 'Emergency Dental',
    icon: '🚨',
    priority: 'high',
    spend: 600,
    patients: '15–22',
    costPerPatient: '$27–40',
    avgCaseValue: 382,
    roi: '9.5x–14x',
    roiEstimateMin: 9.5,
    roiEstimateMax: 14,
    radiusMiles: 5,
    confidence: 94,
    who: 'Adults within 5 miles experiencing urgent dental pain, broken teeth, or dental trauma — searching on their phones, often after hours or on weekends.',
    whyNow:
      'Emergency search volume spiked 22% this month. Two competitors paused their emergency campaigns due to budget exhaustion. Weekend CPC dropped to $0.94 — 40% below weekday average. Your same-day availability is a major competitive advantage right now.',
    keywords: [
      'emergency dentist near me',
      'same day dental appointment',
      'broken tooth repair',
      'dental pain relief',
      'emergency tooth extraction',
    ],
    ad: {
      headline: 'Emergency Dentist — Open Now, Same-Day Visits',
      url: 'smiledentaldowntown.com/emergency',
      description:
        'Tooth pain or broken tooth? We see emergency patients the same day. Walk-ins welcome. Most insurance accepted. Open late & weekends. Call now or book online.',
      extensions: ['📞 Call Now', '📅 Book Online', '📍 Brampton, ON'],
    },
    landingHighlights: [
      'Same-day appointment booking widget',
      '"What to do right now" first aid tips',
      'Insurance checker tool',
      'Google reviews carousel (4.6 stars)',
      'After-hours phone number prominent',
    ],
  },
  {
    id: 2,
    service: 'Dental Implants',
    icon: '🦷',
    priority: 'high',
    spend: 800,
    patients: '3–5',
    costPerPatient: '$160–267',
    avgCaseValue: 4200,
    roi: '15x–26x',
    roiEstimateMin: 15,
    roiEstimateMax: 26,
    radiusMiles: 10,
    confidence: 89,
    who: 'Adults 40–70 within 10 miles researching tooth replacement options — typically comparing implants vs. bridges vs. dentures, often searching cost-related terms.',
    whyNow:
      '"Dental implants cost" searches are up 15% as patients plan spring procedures. No competitor is running weekend implant ads. Your free consultation + 3D scan offer is stronger than any competitor\'s current landing page. Each implant case is worth $4,200.',
    keywords: [
      'dental implants cost',
      'dental implants near me',
      'all-on-4 dentist',
      'tooth replacement options',
      'implant consultation',
    ],
    ad: {
      headline: 'Dental Implants from $89/mo — Free 3D Scan Consultation',
      url: 'smiledentaldowntown.com/implants',
      description:
        'Considering implants? Free consultation includes 3D CT scan and personalized cost estimate. Financing from $89/mo. 5-year warranty. Diamond-certified team.',
      extensions: ['💰 Financing Available', '📷 See Before & After', '⭐ 4.6 Stars (245 Reviews)'],
    },
    landingHighlights: [
      'Cost comparison table (implants vs. bridges vs. dentures)',
      'Before & after gallery',
      'Financing calculator widget',
      'Dr. Chen credentials & experience',
      'Free consultation booking form',
    ],
  },
  {
    id: 3,
    service: 'Teeth Whitening',
    icon: '✨',
    priority: 'high',
    spend: 250,
    patients: '12–18',
    costPerPatient: '$14–21',
    avgCaseValue: 350,
    roi: '16x–25x',
    roiEstimateMin: 16,
    roiEstimateMax: 25,
    radiusMiles: 5,
    confidence: 94,
    who: 'Adults 25–45 within 5 miles searching for cosmetic whitening, often motivated by upcoming events (weddings, photos, job interviews). Highest intent on mobile during evenings.',
    whyNow:
      'Spring seasonal demand — whitening searches up 34% this month. CPC is only $0.85 because 3 competitors paused their whitening ads. Your 4.9-star whitening reviews are the best social proof in the area. Low cost, high volume — this campaign pays for itself within 3 bookings.',
    keywords: [
      'teeth whitening near me',
      'professional teeth whitening',
      'teeth whitening cost',
      'teeth whitening deals',
      'laser teeth whitening',
    ],
    ad: {
      headline: 'Professional Teeth Whitening — $199 Spring Special',
      url: 'smiledentaldowntown.com/whitening',
      description:
        'Brighter smile in one visit. Professional whitening by certified team. Results guaranteed. See before & after photos from real patients. Limited spring pricing.',
      extensions: ['🏷️ $199 Special', '📷 See Results', '📅 Book Today'],
    },
    landingHighlights: [
      'Before & after gallery (real patients)',
      'Method comparison (in-office vs. take-home)',
      '$199 special pricing prominent',
      'FAQ section (sensitivity, duration)',
      'One-click booking widget',
    ],
  },
  {
    id: 4,
    service: 'Invisalign',
    icon: '😁',
    priority: 'medium',
    spend: 500,
    patients: '4–6',
    costPerPatient: '$83–125',
    avgCaseValue: 5500,
    roi: '44x–66x',
    roiEstimateMin: 44,
    roiEstimateMax: 66,
    radiusMiles: 8,
    confidence: 78,
    who: 'Adults 25–50 within 8 miles researching clear aligners, comparing Invisalign to braces, and looking for cost/financing information.',
    whyNow:
      "Bright Smiles paused their Invisalign campaign 2 weeks ago — CPC dropped 22%. Summer is coming and patients want to start treatment before vacations. You're a Diamond-level provider (only 1 of 3 locally) but your website barely mentions it. Medium confidence because Smile Central still dominates organically.",
    keywords: [
      'invisalign near me',
      'invisalign cost',
      'invisalign vs braces',
      'clear aligners dentist',
      'invisalign provider',
    ],
    ad: {
      headline: 'Diamond Invisalign Provider — From $149/mo',
      url: 'smiledentaldowntown.com/invisalign',
      description:
        'Straighten your teeth invisibly. Diamond-certified Invisalign provider — top 1% nationally. Free smile preview before you start. Flexible financing available.',
      extensions: ['🏅 Diamond Provider', '💰 From $149/mo', '📱 Free Smile Preview'],
    },
    landingHighlights: [
      'Diamond provider badge & credentials',
      'Interactive smile preview tool',
      'Treatment timeline visual',
      'Cost & financing breakdown',
      'Patient transformation gallery',
    ],
  },
  {
    id: 5,
    service: 'Spanish — Emergencia',
    icon: '🇪🇸',
    priority: 'medium',
    spend: 150,
    patients: '8–12',
    costPerPatient: '$13–19',
    avgCaseValue: 320,
    roi: '17x–25x',
    roiEstimateMin: 17,
    roiEstimateMax: 25,
    radiusMiles: 5,
    confidence: 91,
    who: "Spanish-speaking adults within 5 miles searching for dental services in their language. 30% of your zip code's population — almost completely underserved by competitors.",
    whyNow:
      'Only 1 competitor runs Spanish ads. CPC for Spanish dental terms is 60% lower than English equivalents. You have 2 bilingual staff members. "Dentista cerca de mi" searches growing 18% quarter over quarter. Near-zero competition means extremely low cost per patient.',
    keywords: [
      'dentista cerca de mi',
      'dentista de emergencia',
      'dentista que habla español',
      'clinica dental brampton',
      'dentista hispano',
    ],
    ad: {
      headline: 'Dentista de Emergencia — Citas el Mismo Día',
      url: 'smiledentaldowntown.com/es/emergencia',
      description:
        'Atención dental de emergencia en Brampton. Hablamos español. Aceptamos la mayoría de seguros. Citas disponibles hoy. Llame ahora o reserve en línea.',
      extensions: ['🗣️ Hablamos Español', '📞 Llame Ahora', '📅 Citas Hoy'],
    },
    landingHighlights: [
      'Full Spanish landing page',
      'Bilingual staff photos & bios',
      'Insurance list in Spanish',
      'Online booking in Spanish',
      'Google reviews in Spanish highlighted',
    ],
  },
];

export const CC_PREVIEW_CAMPAIGN: CcPreviewCampaign = {
  service: 'Pediatric Dentistry',
  icon: '👶',
  budget: 300,
  radiusMiles: 5,
  confidence: 88,
  instructions: 'Focus on back-to-school checkups, target parents with kids under 12',
  target:
    "Parents with children under 12 within 5 miles, searching for kids' dental care, first visits, and back-to-school checkups. Highest intent on mobile during evenings and weekends.",
  whyNow:
    '"Kids dentist" and "back to school dental" searches ramp up every spring — you\'re 6 weeks ahead of peak season. Only 2 of 8 local competitors have pediatric-specific landing pages. Your office has a kids\' play area and multiple reviews mentioning "great with kids" — strong differentiators.',
  patients: '10–15',
  costPerPatient: '$20–30',
  avgCaseValue: 280,
  roi: '9x–14x',
  roiEstimateMin: 4.7,
  roiEstimateMax: 9.3,
  lifetimeNote:
    'Family patients average $4,800 in lifetime value — one child typically brings siblings and parents.',
  landingUrl: 'go.smiledentaldowntown.com/kids',
  keywords: [
    { keyword: 'kids dentist near me', volume: 1200, cpc: 0.85, competition: 'Low' },
    { keyword: 'pediatric dentist brampton', volume: 680, cpc: 0.92, competition: 'Low' },
    { keyword: 'child first dental visit', volume: 520, cpc: 0.78, competition: 'Very Low' },
    { keyword: 'back to school dental checkup', volume: 890, cpc: 0.65, competition: 'Very Low' },
    { keyword: 'family dentist for kids', volume: 440, cpc: 0.88, competition: 'Low' },
    { keyword: 'gentle dentist for children', volume: 310, cpc: 0.72, competition: 'Very Low' },
  ],
  ads: [
    {
      label: 'Variant A',
      headline: "Kids' Dental Checkup — Fun & Gentle First Visits",
      url: 'go.smiledentaldowntown.com/kids',
      description:
        'Back-to-school dental exams for kids. Our child-friendly office has TV, games & a play area. Most insurance accepted. Zero tears guaranteed!',
      extensions: ['👶 Kid-Friendly', '📅 Book Online', '⭐ 4.6 Stars'],
    },
    {
      label: 'Variant B',
      headline: 'First Dental Visit? We Make It Fun for Kids',
      url: 'go.smiledentaldowntown.com/kids',
      description:
        "Nervous about your child's first dentist visit? Our gentle team specializes in pediatric care. Play area, ceiling TVs & patient parents. Book today.",
      extensions: ['🎮 Play Area', '📞 Call Now', '💰 Insurance'],
    },
  ],
  landing: [
    {
      icon: '🎨',
      label: 'Kid-Friendly Office Tour',
      description: 'Photos of play area, ceiling TVs, and colorful treatment rooms',
    },
    {
      icon: '👩‍⚕️',
      label: 'Meet Our Pediatric Team',
      description: 'Doctor bios with child-care certifications highlighted',
    },
    {
      icon: '📋',
      label: 'What to Expect',
      description: 'Step-by-step first visit walkthrough for parents',
    },
    {
      icon: '⭐',
      label: 'Parent Reviews',
      description: 'Filtered Google reviews mentioning kids and family',
    },
    {
      icon: '🛡️',
      label: 'Insurance Checker',
      description: 'Verify coverage before the visit',
    },
    {
      icon: '📅',
      label: 'Book Appointment',
      description: 'Online booking with after-school and weekend slots',
    },
  ],
  tracking: [
    {
      label: 'Dedicated Phone Number',
      description: 'Unique tracking number — every call attributed to this campaign',
    },
    {
      label: 'Landing Page Forms',
      description: 'All submissions logged with name, phone, and service',
    },
    {
      label: 'PMS Integration',
      description: 'Booked appointments automatically matched to campaign',
    },
    {
      label: 'Revenue Attribution',
      description: 'Treatment value tracked for 12 months per patient',
    },
    {
      label: 'Continuous A/B Testing',
      description: '4 page variants tested automatically — winner gets budget',
    },
  ],
};
